/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef, useEffect, useContext, useCallback, useMemo } from 'react';

import { useRouter } from 'libs/router/useRouter';
import { clearTimeoutIfExists } from 'libs/node';

import { ContentContext } from 'context/ContentContext';

import { useProducts } from 'hooks/useProducts';
import { useCreditReportUrl } from 'hooks/useCreditReportUrl';
import { useResizeListener } from 'hooks/useResizeListener';

import { Loader } from 'ui/atoms/Loader/Loader';
import { Faq } from 'ui/atoms/Faq/Faq';
import { NumberedFeatureCards } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards';

import { PricingQuoteHero } from 'features/product-pricing/organisms/PricingQuoteHero/PricingQuoteHero';
import { PricingQuote } from 'features/product-pricing/organisms/PricingQuote/PricingQuote';
import { PricingRegistries } from 'features/product-pricing/organisms/PricingRegistries/PricingRegistries';
import { DataLicensing } from 'features/product-pricing/organisms/DataLicensing/DataLicensing';
import { PricingClients } from 'features/product-pricing/organisms/PricingClients/PricingClients';

import { getPricingFaq } from './ProductPricingPage.faq';
import { PRICING_RELATED } from './ProductPricingPage.related';

const PADDING_BOTTOM_GAP = 150;

const ProductPricingPage = () => {
  const debounceSend = useRef<NodeJS.Timeout>(undefined);
  const { query, push } = useRouter();
  const { secondaryNavigation } = useContext(ContentContext);

  const redirect = useCallback(() => {
    return push(secondaryNavigation.pricing.slug, { lang: `${query.lang}` });
  }, [push, secondaryNavigation.pricing.slug, query.lang]);
  const { loading } = useProducts(redirect);
  const creditReportUrl = useCreditReportUrl();
  const faq = useMemo(() => getPricingFaq(creditReportUrl), [creditReportUrl]);

  const sendDocumentHeight = useCallback(() => {
    if (loading) {
      return;
    }

    const height = document.getElementById('__next')?.offsetHeight || 0;

    if (!window.top) {
      return;
    }

    window.top.postMessage({ height: height + PADDING_BOTTOM_GAP }, '*');
  }, [loading]);

  useEffect(() => {
    const eventVisibilityChange = [
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') {
          debounceSend.current = setTimeout(() => {
            sendDocumentHeight();
          }, 250);
        }
      },
    ] as const;

    document.addEventListener(...eventVisibilityChange);

    return () => {
      clearTimeoutIfExists(debounceSend.current);

      document.removeEventListener(...eventVisibilityChange);
    };
  }, [sendDocumentHeight]);

  const debouncedSendDocumentHeight = useCallback(() => {
    clearTimeoutIfExists(debounceSend.current);

    debounceSend.current = setTimeout(() => {
      sendDocumentHeight();
    }, 250);
  }, [sendDocumentHeight]);

  useResizeListener(() => {
    sendDocumentHeight();
  }, [sendDocumentHeight]);

  if (loading) {
    return <Loader loading height="calc(100vh - 140px)" />;
  }

  return (
    <>
      <PricingQuoteHero />

      <PricingQuote />

      <PricingRegistries />

      <PricingClients ctaHref="#quote" />

      <DataLicensing />

      <NumberedFeatureCards title="Before you talk to anyone." items={PRICING_RELATED} columns={3} theme="dark" />

      <Faq
        className="pb-55"
        title="Questions buyers ask before the call."
        data={faq}
        onClick={debouncedSendDocumentHeight}
      />
    </>
  );
};

/**
 * please, ignore export default error
 * Only pages require to have export default as they are imported dynamically
 */

// eslint-disable-next-line import/no-default-export
export default ProductPricingPage;
